"use client";

import Link from "next/link";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@stocks/ui";
import { PURCHASE_ORDER_STATUS } from "@stocks/shared";

import {
  MaterialsTable,
  OrderStatusBadge,
  formatDate,
  formatOrderQuantity,
  usePurchaseOrder,
  useRequirements,
} from "@/entities/order";
import { CancelOrderButton } from "@/features/order/cancel-order";

/** 발주 상세 — 기본 정보, 제품 행, 서버가 확정 계산한 필요 자재·부족 수량 */
export const OrderDetail = ({ id }: { id: string }) => {
  const { data: order, isLoading, isError } = usePurchaseOrder(id);
  const { data: req, isLoading: reqLoading } = useRequirements(id);

  if (isLoading) return <p className='text-muted-foreground'>불러오는 중…</p>;
  if (isError || !order)
    return <p className='text-destructive'>발주를 찾을 수 없습니다.</p>;

  const open = order.status === PURCHASE_ORDER_STATUS.OPEN;

  return (
    <div className='flex max-w-5xl flex-col gap-6'>
      <Card>
        <CardHeader className='flex flex-row items-center justify-between gap-2'>
          <CardTitle className='flex items-center gap-2'>
            {order.orderNo} <OrderStatusBadge status={order.status} />
          </CardTitle>
          <div className='flex gap-2'>
            {open && (
              <>
                <Button asChild variant='outline'>
                  <Link href={`/orders/${order.id}/edit`}>수정</Link>
                </Button>
                <CancelOrderButton orderId={order.id} orderNo={order.orderNo} />
              </>
            )}
            <Button asChild variant='outline'>
              <Link href='/orders'>목록</Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <dl className='grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2'>
            <dt className='text-muted-foreground'>고객</dt>
            <dd>
              {order.customerCode} · {order.customerName}
            </dd>
            <dt className='text-muted-foreground'>납기일</dt>
            <dd>{formatDate(order.dueDate)}</dd>
            <dt className='text-muted-foreground'>등록일</dt>
            <dd>{formatDate(order.createdAt)}</dd>
            <dt className='text-muted-foreground'>메모</dt>
            <dd>{order.memo ?? "-"}</dd>
            {!open && (
              <>
                <dt className='text-muted-foreground'>취소일</dt>
                <dd>{formatDate(order.cancelledAt)}</dd>
                <dt className='text-muted-foreground'>취소 사유</dt>
                <dd>{order.cancelReason ?? "-"}</dd>
              </>
            )}
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>발주 제품</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>제품</TableHead>
                <TableHead>색상</TableHead>
                <TableHead>사이즈</TableHead>
                <TableHead className='text-right'>수량 (벌)</TableHead>
                <TableHead>BOM 버전</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {order.lines.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>
                    {l.productCode} · {l.productName}
                  </TableCell>
                  <TableCell>{l.color}</TableCell>
                  <TableCell>{l.size}</TableCell>
                  <TableCell className='text-right'>
                    {formatOrderQuantity(l.quantity)}
                  </TableCell>
                  <TableCell>{l.bomVersion ? `v${l.bomVersion}` : "미등록"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>필요 자재 · 부족 수량</CardTitle>
        </CardHeader>
        <CardContent className='flex flex-col gap-3'>
          {reqLoading && <p className='text-muted-foreground'>계산 중…</p>}
          {req && req.linesWithoutBom.length > 0 && (
            <p className='text-sm text-destructive'>
              BOM 이 없어 계산하지 못한 행: {req.linesWithoutBom.join(", ")}
            </p>
          )}
          {req && (
            <>
              <MaterialsTable
                rows={req.materials.map((m) => ({
                  ...m,
                  required: m.requiredQuantity,
                  available: m.availableQuantity,
                  shortage: m.shortageQuantity,
                }))}
              />
              <p
                className={`text-sm ${req.hasShortage ? "font-medium text-destructive" : "text-muted-foreground"}`}
              >
                {req.hasShortage
                  ? "부족한 자재가 있습니다. 입고 후 보충하세요."
                  : "모든 자재의 가용 수량이 충분합니다."}
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
